import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { resolveActiveCompany } from "../utils/activeCompanyGuard";
import {
  homePath,
  loginPath,
  normalizeAlias,
  readLastAlias,
} from "../utils/contractPath";
import { userHasAnyAuth } from "../utils/userRoles";
import { AppHeaderBar } from "./shared/AppHeaderBar/AppHeaderBar";
import { AppShell } from "./shared/AppShell/AppShell";

/**
 * Destino quando a URL tem alias diferente da sessão autenticada.
 * Pure — regressão do bug «/dandg com sessão gfandf → home GF&F».
 *
 * @returns {"ok"|"login_requested_alias"}
 */
export function resolveProtectedContractMatch({ sessionAlias, urlAlias }) {
  const session = normalizeAlias(sessionAlias);
  const url = normalizeAlias(urlAlias);
  if (session && url && session !== url) return "login_requested_alias";
  return "ok";
}

/**
 * Gate de rota por `requireAuth` (código RD ou lista) alinhado a
 * `tb_sca_menu` / `user.authorizations`.
 */
export function ProtectedRoute({
  children,
  requireAuth = null,
  requireActiveCompany = false,
  requireModule = null,
  requireSubmodule = null,
}) {
  const { isAuthenticated, authReady, user } = useAuth();
  const { cdAlias } = useParams();
  const alias =
    normalizeAlias(cdAlias) ||
    normalizeAlias(user?.cd_alias) ||
    readLastAlias();
  const home = homePath(alias);
  const login = loginPath(alias);

  if (!authReady && !user) return null;
  if (!isAuthenticated) {
    return <Navigate to={login || "/"} replace />;
  }

  const sessionAlias = normalizeAlias(user?.cd_alias);
  if (
    resolveProtectedContractMatch({
      sessionAlias,
      urlAlias: alias,
    }) === "login_requested_alias"
  ) {
    return <Navigate to={loginPath(alias) || "/"} replace />;
  }

  if (requireModule) {
    const enabled = Array.isArray(user?.enabled_modules)
      ? user.enabled_modules.map((c) => String(c).trim().toUpperCase())
      : [];
    if (!enabled.includes(String(requireModule).trim().toUpperCase())) {
      return <Navigate to={home} replace />;
    }
  }
  if (requireSubmodule) {
    const enabled = Array.isArray(user?.enabled_submodules)
      ? user.enabled_submodules.map((c) => String(c).trim().toUpperCase())
      : [];
    const needed = Array.isArray(requireSubmodule)
      ? requireSubmodule.map((c) => String(c).trim().toUpperCase())
      : [String(requireSubmodule).trim().toUpperCase()];
    const ok = needed.some((c) => c && enabled.includes(c));
    if (!ok) {
      return <Navigate to={home} replace />;
    }
  }
  if (requireAuth != null && requireAuth !== "") {
    if (!userHasAnyAuth(user, requireAuth)) {
      return <Navigate to={home} replace />;
    }
  }
  if (requireActiveCompany && !resolveActiveCompany(user).isValid) {
    const permitted = user?.permitted_companies ?? [];
    return (
      <AppShell>
        <div className="usuarios">
          <AppHeaderBar title="Empresa" />
          <div className="app-header-company-block">
            {permitted.length === 0 ? (
              <p>
                Nenhuma empresa vinculada ao seu usuário. Contate o administrador.
              </p>
            ) : (
              <p>
                Selecione uma empresa no canto superior direito para usar cadastros e
                lançamentos vinculados à empresa ativa.
              </p>
            )}
          </div>
        </div>
      </AppShell>
    );
  }
  return <AppShell>{children}</AppShell>;
}
